import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { OnboardingLanguage } from './constants';
import type { GameMode, Pillar as OnboardingPillar, StepId } from './state';
import { GAME_MODE_META } from '../lib/gameModeMeta';
import { apiAuthorizedFetch, getCurrentUserProfile, markOnboardingProgress } from '../lib/api';
import { setJourneyGenerationPending } from '../lib/journeyGeneration';
import { buildDemoUrl } from '../lib/demoEntry';
import { GpExplainerOverlay } from './ui/GpExplainerOverlay';
import { HUD } from './ui/HUD';
import { NavButtons } from './ui/NavButtons';
import { useThemePreference } from '../theme/ThemePreferenceProvider';
import {
  QUICK_START_MINIMUMS,
  QUICK_START_TASKS,
  computeQuickStartGp,
  getQuickStartSuggestedRule,
  type ModerationOption,
} from './quickStart';
import { QuickStartModerationStep } from './steps/QuickStartModerationStep';
import { QuickStartSummaryStep } from './steps/QuickStartSummaryStep';
import { QuickStartTasksStep } from './steps/QuickStartTasksStep';

type Pillar = 'Body' | 'Mind' | 'Soul';
type Step = 'body' | 'mind' | 'soul' | 'moderation' | 'summary' | 'setup';

interface Task {
  id: string;
  trait: string;
  text: string;
  inputBefore?: string;
  inputAfter?: string;
  helper?: string;
  suggestions?: string[];
}

interface Translations {
  title: string;
  subtitle: string;
  forkTitle: string;
  forkSubtitle: string;
  personalGuide: string;
  personalGuidePrimary: string;
  personalGuideTime: string;
  personalGuideHint: string;
  quickStart: string;
  quickStartPrimary: string;
  quickStartTime: string;
  quickStartHint: string;
  inPreview: string;
  comingSoon: string;
  gameModeGateTitle: string;
  gameModeGateSubtitle: string;
  continue: string;
  back: string;
  pillarTitles: Record<Pillar, string>;
  pillarSubtitles: Record<Pillar, string>;
  minRule: (min: number) => string;
  suggestedRule: (gameMode: GameMode) => string;
  minimumRequired: string;
  traitLabel: string;
  taskHelpLabel: string;
  minutesPlaceholder: string;
  countPlaceholder: string;
  moderationTitle: string;
  moderationSubtitle: string;
  moderationHint: string;
  moderationFlex: string;
  setupTitle: string;
  setupSubtitle: string;
  setupBridgeHint: string;
  setupDone: string;
  setupCta: string;
  quickSummary: {
    eyebrow: string;
    title: string;
    subtitle: string;
    baseData: string;
    gameMode: string;
    state: string;
    pillars: string;
    selectedTraits: string;
    xp: string;
    baseHint: string;
    selectedTasks: string;
    total: string;
    start: string;
  };
  bonusReady: string;
  bonusPending: string;
  modeLabels: Record<GameMode, string>;
  moderationCards: Record<ModerationOption, { title: string; description: string; icon: string }>;
  setupSteps: string[];
  tasks: Record<Pillar, Task[]>;
}



interface QuickStartPreviewDraft {
  selectedGameMode: GameMode;
  onboardingPath: 'quick_start';
  selectedTasksByPillar: Record<Pillar, string[]>;
  selectedTraitsByPillar: Record<Pillar, string[]>;
  editableTaskValues: Record<string, string>;
  selectedModerations: ModerationOption[];
  language: OnboardingLanguage;
  gpSummaryPreview: {
    byPillar: Record<Pillar, number>;
    total: number;
  };
  balanceBonusPreview: {
    active: boolean;
    multiplier: number;
  };
  completedSteps: Partial<Record<Step, boolean>>;
}

const COPY: Record<OnboardingLanguage, Translations> = {
  es: {
    title: 'Inicio rápido',
    subtitle: '',
    forkTitle: '¿Cómo querés arrancar hoy?',
    forkSubtitle: 'Elegí el camino que mejor se adapta a cómo querés empezar hoy.',
    personalGuide: 'Guía personal',
    personalGuidePrimary: 'Respondé un cuestionario y la IA arma tu sistema.',
    personalGuideTime: '3–4 min',
    personalGuideHint: 'Camino guiado y más personalizado, con el acompañamiento completo.',
    quickStart: 'Inicio rápido',
    quickStartPrimary: 'Empezá con tareas recomendadas.',
    quickStartTime: '< 1 min',
    quickStartHint: 'Camino más rápido y autodirigido para arrancar hoy con tu base.',
    inPreview: 'Quick Start',
    comingSoon: 'Próximamente',
    gameModeGateTitle: 'Primero elegí tu ritmo',
    gameModeGateSubtitle: 'Usamos la misma pantalla real del onboarding para mantener consistencia.',
    continue: 'Continuar',
    back: 'Volver',
    pillarTitles: {
      Body: 'Cuerpo 🫀',
      Mind: 'Mente 🧠',
      Soul: 'Alma 🏵️',
    },
    pillarSubtitles: {
      Body: 'Elegí 10 tareas visibles y activá las que querés sostener.',
      Mind: 'Ajustá tu foco mental sin salir del ritmo del onboarding actual.',
      Soul: 'Definí hábitos que te ayuden a mantener centro y conexión.',
    },
    minRule: (min) => `Mínimo: ${min} tareas`,
    suggestedRule: (gameMode) => getQuickStartSuggestedRule(gameMode, 'es'),
    minimumRequired: 'Necesitás seleccionar el mínimo para continuar.',
    traitLabel: 'RASGO',
    taskHelpLabel: 'Abrir ideas rápidas de la tarea',
    minutesPlaceholder: 'min',
    countPlaceholder: 'n',
    moderationTitle: 'Moderación',
    moderationSubtitle: 'Elegí qué querés observar con más consciencia.',
    moderationHint: 'Sin juicios: solo seguimiento para encontrar balance.',
    moderationFlex: 'También podés manejar tolerancias flexibles (por ejemplo, fines de semana).',
    setupTitle: 'Configurando tu Inicio rápido',
    setupSubtitle: 'Estamos aplicando tu selección y reutilizando el flujo real del onboarding.',
    setupBridgeHint: 'Continúa hacia la demo guiada para conocer más de Innerbloom. Puedes saltarla cuando quieras.',
    setupDone: '',
    setupCta: 'Ir a la demo',
    quickSummary: {
      eyebrow: 'Resumen',
      title: 'Tu base inicial',
      subtitle: 'Versión compacta antes de activar tu Journey.',
      baseData: 'Base data',
      gameMode: 'Ritmo',
      state: 'Estado',
      pillars: 'Pilares',
      selectedTraits: 'Rasgos de tus tareas seleccionadas:',
      xp: 'GP (Puntos de Crecimiento)',
      baseHint: 'Esta configuración será tu base inicial para arrancar y ajustarla luego.',
      selectedTasks: 'Tareas seleccionadas',
      total: 'Total',
      start: 'Comienza tu Journey',
    },
    bonusReady: 'Balanceado: estás sumando x1.5 GP',
    bonusPending: 'Balanceá Cuerpo, Mente y Alma para sumar x1.5 GP',
    modeLabels: {
      LOW: 'Low',
      CHILL: 'Chill',
      FLOW: 'Flow',
      EVOLVE: 'Evolve',
    },
    moderationCards: {
      sugar: {
        title: 'Azúcar',
        description: 'Registrar cuándo aparece antojo o exceso para mejorar decisiones.',
        icon: '🍬',
      },
      tobacco: {
        title: 'Tabaco',
        description: 'Seguir consumo y contexto para recortar de forma sostenible.',
        icon: '🚭',
      },
      alcohol: {
        title: 'Alcohol',
        description: 'Observar frecuencia y momentos para mantener equilibrio.',
        icon: '🍷',
      },
    },
    setupSteps: [
      'Equilibrando Cuerpo, Mente y Alma',
      'Calibrando la dificultad de tus tareas',
      'Preparando tu plan inicial',
      'Activando tu ritmo: {mode}',
      'Activando tu plan – 2 meses incluidos',
    ],
    tasks: {
      Body: [
        { id: 'ENERGIA', trait: 'ENERGIA', text: 'Caminar durante', inputAfter: 'minutos' },
        { id: 'NUTRICION', trait: 'NUTRICION', text: 'Hacer', inputAfter: 'comida(s) equilibrada(s) al día' },
        { id: 'SUENO', trait: 'SUENO', text: 'Dormir al menos', inputAfter: 'horas por noche' },
        {
          id: 'RECUPERACION',
          trait: 'RECUPERACION',
          text: 'Hacer una pausa de recuperación durante el día',
          suggestions: ['frenar unos minutos', 'cerrar los ojos', 'respirar', 'bajar estímulos', 'estirar un poco'],
        },
        { id: 'HIDRATACION', trait: 'HIDRATACION', text: 'Tomar', inputAfter: 'vaso(s) de agua al día' },
        {
          id: 'HIGIENE',
          trait: 'HIGIENE',
          text: 'Completar mi rutina de higiene personal',
          suggestions: ['lavarme la cara', 'bañarme', 'lavarme los dientes', 'ponerme crema', 'ordenar mi cuidado básico'],
        },
        {
          id: 'VITALIDAD',
          trait: 'VITALIDAD',
          text: 'Empezar el día con una rutina activadora',
          suggestions: ['abrir la ventana', 'mover el cuerpo', 'tomar agua', 'estirarme', 'evitar arrancar con el móvil'],
        },
        {
          id: 'POSTURA',
          trait: 'POSTURA',
          text: 'Cuidar mi postura o ergonomía',
          suggestions: ['sentarme mejor', 'ajustar la pantalla', 'apoyar bien la espalda', 'cambiar de posición', 'revisar cómo trabajo'],
        },
        { id: 'MOVILIDAD', trait: 'MOVILIDAD', text: 'Hacer movilidad o estiramientos durante', inputAfter: 'minutos' },
        { id: 'MODERACION', trait: 'MODERACION', text: 'Mejorar mi relación con ciertos consumos o excesos' },
      ],
      Mind: [
        { id: 'ENFOQUE', trait: 'ENFOQUE', text: 'Trabajar con foco profundo durante', inputAfter: 'minutos' },
        { id: 'APRENDIZAJE', trait: 'APRENDIZAJE', text: 'Leer, estudiar o aprender durante', inputAfter: 'minutos' },
        { id: 'CREATIVIDAD', trait: 'CREATIVIDAD', text: 'Dedicar', inputAfter: 'minutos a crear, escribir o idear' },
        {
          id: 'GESTION',
          trait: 'GESTION',
          text: 'Tomarme un momento para respirar, bajar revoluciones o regularme',
          suggestions: ['respirar profundo', 'salir del ruido', 'pausar', 'soltar tensión', 'bajar ansiedad'],
        },
        {
          id: 'AUTOCONTROL',
          trait: 'AUTOCONTROL',
          text: 'Prestar atención a mis acciones impulsivas',
          suggestions: ['notar cuándo reacciono rápido', 'observar impulsos', 'frenar antes de responder', 'detectar automatismos'],
        },
        {
          id: 'RESILIENCIA',
          trait: 'RESILIENCIA',
          text: 'Hacer algo que me desafíe o me saque de mi zona de confort',
          suggestions: ['iniciar una conversación difícil', 'probar algo nuevo', 'hacer algo que vengo evitando', 'sostener una incomodidad útil'],
        },
        {
          id: 'ORDEN',
          trait: 'ORDEN',
          text: 'Ordenar mi espacio, mis tareas o mi mente',
          suggestions: ['ordenar el escritorio', 'vaciar pendientes mentales', 'limpiar una superficie', 'reorganizar tareas', 'poner algo en su lugar'],
        },
        {
          id: 'PROYECCION',
          trait: 'PROYECCION',
          text: 'Dar un paso hacia una meta personal o profesional',
          suggestions: ['enviar un mensaje importante', 'avanzar una idea', 'actualizar CV o portfolio', 'terminar una tarea clave', 'mover una meta un paso'],
        },
        {
          id: 'FINANZAS',
          trait: 'FINANZAS',
          text: 'Revisar mis gastos, ahorro o presupuesto',
          suggestions: ['mirar movimientos', 'registrar gastos', 'revisar presupuesto', 'controlar suscripciones', 'ver cuánto ahorré'],
        },
        { id: 'AGILIDAD', trait: 'AGILIDAD', text: 'Entrenar mi memoria o agilidad mental durante', inputAfter: 'minutos' },
      ],
      Soul: [
        { id: 'CONEXION', trait: 'CONEXION', text: 'Hablar con', inputAfter: 'persona(s) con presencia real' },
        { id: 'ESPIRITUALIDAD', trait: 'ESPIRITUALIDAD', text: 'Dedicar', inputAfter: 'minutos a meditar, rezar o conectar conmigo' },
        {
          id: 'PROPOSITO',
          trait: 'PROPOSITO',
          text: 'Hacer una acción alineada con mi propósito',
          suggestions: ['avanzar algo importante', 'elegir con intención', 'hacer algo que tenga sentido para mí', 'dedicar tiempo a lo que valoro'],
        },
        {
          id: 'VALORES',
          trait: 'VALORES',
          text: 'Tomar una decisión alineada con mis valores',
          suggestions: ['decir que no a algo que no va conmigo', 'elegir con coherencia', 'actuar como quiero ser', 'sostener un criterio importante'],
        },
        {
          id: 'ALTRUISMO',
          trait: 'ALTRUISMO',
          text: 'Hacer un gesto de ayuda o aporte a otros',
          suggestions: ['ayudar a alguien', 'escribir a alguien que lo necesite', 'compartir algo útil', 'colaborar', 'acompañar'],
        },
        {
          id: 'INSIGHT',
          trait: 'INSIGHT',
          text: 'Reflexionar sobre cómo me siento',
          suggestions: ['notar cómo me sentí hoy', 'identificar qué necesito', 'observar qué me afectó', 'darme un momento para escucharme'],
        },
        { id: 'GRATITUD', trait: 'GRATITUD', text: 'Registrar', inputAfter: 'cosa(s) por las que siento gratitud' },
        {
          id: 'NATURALEZA',
          trait: 'NATURALEZA',
          text: 'Conectar con la naturaleza',
          suggestions: ['salir a tomar aire', 'mirar el cielo', 'caminar entre árboles', 'sentarme al sol', 'conectar con algo natural'],
        },
        { id: 'GOZO', trait: 'GOZO', text: 'Dedicar', inputAfter: 'minutos a jugar, reír o disfrutar sin culpa' },
        {
          id: 'AUTOESTIMA',
          trait: 'AUTOESTIMA',
          text: 'Tomarme tiempo para cuidar de mí',
          suggestions: ['cortarme las uñas', 'peinarme', 'arreglarme el pelo', 'cuidarme la barba', 'vestirme con más cariño', 'hacer algo que me haga bien'],
        },
      ],
    },
  },
  en: {
    title: 'Quick Start',
    subtitle: 'Set up your initial base with the integrated fast onboarding flow.',
    forkTitle: 'How do you want to start today?',
    forkSubtitle: 'Choose the path that best matches how you want to start today.',
    personalGuide: 'Personal guide',
    personalGuidePrimary: 'Answer a questionnaire and AI builds your system.',
    personalGuideTime: '3–4 min',
    personalGuideHint: 'A more guided and personalized path, with full support along the way.',
    quickStart: 'Quick start',
    quickStartPrimary: 'Start with recommended tasks.',
    quickStartTime: '< 1 min',
    quickStartHint: 'A faster, self-directed path to get started today with your base.',
    inPreview: 'Quick Start',
    comingSoon: 'Coming soon',
    gameModeGateTitle: 'First, choose your Rhythm',
    gameModeGateSubtitle: 'This step reuses the same real Rhythm screen from onboarding.',
    continue: 'Continue',
    back: 'Back',
    pillarTitles: {
      Body: 'Body 🫀',
      Mind: 'Mind 🧠',
      Soul: 'Soul 🏵️',
    },
    pillarSubtitles: {
      Body: 'Review all 10 tasks and activate what you want to sustain.',
      Mind: 'Tune your mental focus while keeping the current onboarding feel.',
      Soul: 'Choose habits that support connection and inner alignment.',
    },
    minRule: (min) => `Minimum: ${min} tasks`,
    suggestedRule: (gameMode) => getQuickStartSuggestedRule(gameMode, 'en'),
    minimumRequired: 'You need to select the minimum to continue.',
    traitLabel: 'TRAIT',
    taskHelpLabel: 'Open quick ideas for this task',
    minutesPlaceholder: 'min',
    countPlaceholder: 'qty',
    moderationTitle: 'Moderation',
    moderationSubtitle: 'Choose what you want to track with more awareness.',
    moderationHint: 'No judgment: only tracking to find your own balance.',
    moderationFlex: 'You can also use flexible tolerance settings (for example, weekends).',
    setupTitle: 'Configuring your Quick Start',
    setupSubtitle: 'Applying your selection with the same calibration feel used in onboarding.',
    setupBridgeHint: 'Next, you\'ll enter the guided demo to explore how Innerbloom is structured. You can skip it anytime.',
    setupDone: '',
    setupCta: 'Go to guided demo',
    quickSummary: {
      eyebrow: 'Summary',
      title: 'Your initial base',
      subtitle: 'Compact review before activating your Journey.',
      baseData: 'Base data',
      gameMode: 'Rhythm',
      state: 'State',
      pillars: 'Pillars',
      selectedTraits: 'Traits from your selected tasks:',
      xp: 'GP (Growth Points)',
      baseHint: 'This setup will be your initial base so you can start and adjust later.',
      selectedTasks: 'Selected tasks',
      total: 'Total',
      start: 'Start your Journey',
    },
    bonusReady: 'Balanced: you are earning x1.5 GP',
    bonusPending: 'Balance Body, Mind, and Soul to earn x1.5 GP',
    modeLabels: {
      LOW: 'Low',
      CHILL: 'Chill',
      FLOW: 'Flow',
      EVOLVE: 'Evolve',
    },
    moderationCards: {
      sugar: {
        title: 'Sugar',
        description: 'Track cravings or overuse moments to make better decisions.',
        icon: '🍬',
      },
      tobacco: {
        title: 'Tobacco',
        description: 'Track consumption and context to reduce it sustainably.',
        icon: '🚭',
      },
      alcohol: {
        title: 'Alcohol',
        description: 'Observe frequency and triggers to keep things in balance.',
        icon: '🍷',
      },
    },
    setupSteps: [
      'Balancing Body, Mind and Soul',
      'Calibrating your task difficulty',
      'Preparing your initial plan',
      'Activating your Rhythm: {mode}',
      'Activating your plan – 2 months included',
    ],
    tasks: {
      Body: [
        { id: 'ENERGIA', trait: 'ENERGY', text: 'Walk for', inputAfter: 'minutes' },
        { id: 'NUTRICION', trait: 'NUTRITION', text: 'Have', inputAfter: 'balanced meal(s) per day' },
        { id: 'SUENO', trait: 'SLEEP', text: 'Sleep at least', inputAfter: 'hours per night' },
        {
          id: 'RECUPERACION',
          trait: 'RECOVERY',
          text: 'Take a recovery break during the day',
          suggestions: ['pause for a few minutes', 'close your eyes', 'breathe slowly', 'lower stimulation', 'do a quick stretch'],
        },
        { id: 'HIDRATACION', trait: 'HYDRATION', text: 'Drink', inputAfter: 'glass(es) of water per day' },
        {
          id: 'HIGIENE',
          trait: 'HYGIENE',
          text: 'Complete my personal hygiene routine',
          suggestions: ['wash my face', 'take a shower', 'brush my teeth', 'apply moisturizer', 'reset my basic self-care'],
        },
        {
          id: 'VITALIDAD',
          trait: 'VITALITY',
          text: 'Start the day with an activating routine',
          suggestions: ['open the window', 'move my body', 'drink water', 'do a quick stretch', 'avoid starting with my phone'],
        },
        {
          id: 'POSTURA',
          trait: 'POSTURE',
          text: 'Take care of my posture or ergonomics',
          suggestions: ['sit better', 'adjust screen height', 'support my lower back', 'change position', 'check how I am working'],
        },
        { id: 'MOVILIDAD', trait: 'MOBILITY', text: 'Do mobility or stretching for', inputAfter: 'minutes' },
        { id: 'MODERACION', trait: 'MODERATION', text: 'Improve my relationship with certain consumptions or excesses' },
      ],
      Mind: [
        { id: 'ENFOQUE', trait: 'FOCUS', text: 'Do deep focus work for', inputAfter: 'minutes' },
        { id: 'APRENDIZAJE', trait: 'LEARNING', text: 'Read, study, or learn for', inputAfter: 'minutes' },
        { id: 'CREATIVIDAD', trait: 'CREATIVITY', text: 'Spend', inputAfter: 'minutes creating, writing, or ideating' },
        {
          id: 'GESTION',
          trait: 'MANAGEMENT',
          text: 'Take a moment to breathe, slow down, or self-regulate',
          suggestions: ['take a deep breath', 'step away from noise', 'pause briefly', 'release tension', 'lower anxiety'],
        },
        {
          id: 'AUTOCONTROL',
          trait: 'SELF_CONTROL',
          text: 'Notice my impulsive actions',
          suggestions: ['notice fast reactions', 'observe impulses', 'pause before responding', 'spot automatic patterns'],
        },
        {
          id: 'RESILIENCIA',
          trait: 'RESILIENCE',
          text: 'Do something that challenges me outside my comfort zone',
          suggestions: ['start a hard conversation', 'try something new', 'do something I have been avoiding', 'hold useful discomfort'],
        },
        {
          id: 'ORDEN',
          trait: 'ORDER',
          text: 'Organize my space, tasks, or mind',
          suggestions: ['tidy my desk', 'empty mental clutter', 'clean one surface', 'reorder priorities', 'put one thing back in place'],
        },
        {
          id: 'PROYECCION',
          trait: 'PROJECTION',
          text: 'Take one step toward a personal or professional goal',
          suggestions: ['send one important message', 'move one idea forward', 'update CV or portfolio', 'finish one key task', 'advance one goal by one step'],
        },
        {
          id: 'FINANZAS',
          trait: 'FINANCES',
          text: 'Review my spending, savings, or budget',
          suggestions: ['check recent movements', 'log expenses', 'review my budget', 'audit subscriptions', 'check how much I saved'],
        },
        { id: 'AGILIDAD', trait: 'AGILITY', text: 'Train my memory or mental agility for', inputAfter: 'minutes' },
      ],
      Soul: [
        { id: 'CONEXION', trait: 'CONNECTION', text: 'Talk with', inputAfter: 'person(s) with real presence' },
        { id: 'ESPIRITUALIDAD', trait: 'SPIRITUALITY', text: 'Spend', inputAfter: 'minutes meditating, praying, or reconnecting with myself' },
        {
          id: 'PROPOSITO',
          trait: 'PURPOSE',
          text: 'Take one action aligned with my purpose',
          suggestions: ['move something meaningful', 'choose with intention', 'do something that truly matters to me', 'make time for what I value'],
        },
        {
          id: 'VALORES',
          trait: 'VALUES',
          text: 'Make a decision aligned with my values',
          suggestions: ['say no to something that does not fit me', 'choose with integrity', 'act like the person I want to be', 'hold one important boundary'],
        },
        {
          id: 'ALTRUISMO',
          trait: 'ALTRUISM',
          text: 'Do one helpful gesture for someone else',
          suggestions: ['help someone', 'message someone who may need support', 'share something useful', 'collaborate', 'be present for someone'],
        },
        {
          id: 'INSIGHT',
          trait: 'INSIGHT',
          text: 'Reflect on how I feel',
          suggestions: ['notice how I felt today', 'identify what I need', 'observe what affected me', 'give myself a moment to listen inward'],
        },
        { id: 'GRATITUD', trait: 'GRATITUDE', text: 'Log', inputAfter: 'thing(s) I feel grateful for' },
        {
          id: 'NATURALEZA',
          trait: 'NATURE',
          text: 'Connect with nature',
          suggestions: ['go out for fresh air', 'look at the sky', 'walk near trees', 'sit under sunlight', 'connect with something natural'],
        },
        { id: 'GOZO', trait: 'JOY', text: 'Spend', inputAfter: 'minutes playing, laughing, or enjoying without guilt' },
        {
          id: 'AUTOESTIMA',
          trait: 'SELF_ESTEEM',
          text: 'Take time to care for myself',
          suggestions: ['trim my nails', 'comb my hair', 'style my hair', 'groom my beard', 'dress with more care', 'do something that makes me feel good'],
        },
      ],
    },
  },
};

const STEP_ORDER: Step[] = ['body', 'mind', 'soul', 'moderation', 'summary', 'setup'];


const GAME_MODE_META_KEY: Record<GameMode, keyof typeof GAME_MODE_META> = {
  LOW: 'Low',
  CHILL: 'Chill',
  FLOW: 'Flow',
  EVOLVE: 'Evolve',
};

function buildVisibleRoute(includeModeration: boolean): Step[] {
  return includeModeration ? STEP_ORDER : STEP_ORDER.filter((step) => step !== 'moderation');
}

function InlineTaskRow({
  task,
  selected,
  inputValue,
  onToggle,
  onInputChange,
  copy,
  mode,
}: {
  task: Task;
  selected: boolean;
  inputValue: string;
  onToggle: () => void;
  onInputChange: (value: string) => void;
  copy: Translations;
  mode: GameMode;
}) {
  const hasInput = Boolean(task.inputAfter || task.inputBefore);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selected) {
      setShowSuggestions(false);
    }
  }, [selected]);

  useEffect(() => {
    if (!showSuggestions) {
      return undefined;
    }

    const handleOutsidePointer = (event: PointerEvent) => {
      if (!rowRef.current?.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => document.removeEventListener('pointerdown', handleOutsidePointer);
  }, [showSuggestions]);


  return (
    <div ref={rowRef} className={`relative ${selected ? 'pt-5 pb-1' : ''}`}>
      {selected ? (
        <div className="quickstart-task-row__back pointer-events-none absolute inset-x-0 top-0 h-8 rounded-t-2xl border px-4 pt-1.5" aria-hidden>
          <span className="quickstart-task-trait-band block text-[10px] font-semibold uppercase leading-none tracking-[0.16em]">
            {copy.traitLabel}: {task.trait}
          </span>
        </div>
      ) : null}

      <motion.div
        whileTap={{ scale: 0.985 }}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        data-selected={selected ? 'true' : undefined}
        className={`quickstart-task-row onboarding-surface-inner relative z-10 w-full rounded-2xl border px-4 py-3.5 text-left text-white/85 transition ${selected ? 'quickstart-task-row--selected' : ''}`}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-2 text-sm leading-relaxed sm:text-base">
            {task.inputBefore ? <span>{task.inputBefore}</span> : null}
            <span>{task.text}</span>
            {hasInput ? (
              <input
                value={inputValue}
                disabled={!selected}
                inputMode="numeric"
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => onInputChange(event.target.value)}
                className="quickstart-task-input h-6 w-12 rounded-md border px-1.5 text-center text-xs focus:outline-none focus-visible:ring-2 disabled:opacity-40 sm:h-7 sm:w-14"
                placeholder={copy.countPlaceholder}
              />
            ) : null}
            {task.inputAfter ? <span>{task.inputAfter}</span> : null}
            {task.helper ? <span className="w-full text-xs text-white/55">{task.helper}</span> : null}
          </div>

          {selected && task.suggestions?.length ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setShowSuggestions((prev) => !prev);
              }}
              className="quickstart-task-help relative z-20 ml-auto inline-flex h-5 w-5 shrink-0 items-center justify-center self-start rounded-md border transition"
              aria-label={copy.taskHelpLabel}
              aria-expanded={showSuggestions}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3" aria-hidden>
                <path d="M9 18h6" />
                <path d="M10 22h4" />
                <path d="M8.5 14.3a6 6 0 1 1 7 0c-.7.53-1.17 1.35-1.3 2.2l-.08.5h-4.24l-.08-.5a3.5 3.5 0 0 0-1.3-2.2Z" />
              </svg>
            </button>
          ) : null}
        </div>
      </motion.div>

      {selected && task.suggestions?.length && showSuggestions ? (
        <div
          className="quickstart-suggestions-panel absolute right-3 bottom-[calc(100%+0.35rem)] z-30 w-[min(18.5rem,calc(100%-1.5rem))] rounded-xl border p-3 text-xs backdrop-blur"
        >
          <ul className="space-y-1.5">
            {task.suggestions.map((suggestion) => (
              <li key={suggestion} className="leading-relaxed">• {suggestion}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

interface IntegratedQuickStartFlowProps {
  language?: OnboardingLanguage;
  gameMode: GameMode;
  avatarId: number | null;
  onBackToPathSelect: () => void;
  onExit: () => void;
  onRestart: () => void;
  routeStepId?: StepId;
  selectedTasksByPillar?: Record<OnboardingPillar, string[]>;
  editableTaskValues?: Record<string, string>;
  selectedModerations?: ModerationOption[];
  isSubmitting?: boolean;
  submitError?: string | null;
  onToggleTask?: (pillar: OnboardingPillar, taskId: string) => void;
  onTaskInputChange?: (taskKey: string, value: string) => void;
  onToggleModeration?: (value: ModerationOption) => void;
  onBack?: () => void;
  onConfirm?: () => void;
  onFinish?: () => void;
}

function ControlledQuickStartFlow({
  language,
  gameMode,
  routeStepId,
  selectedTasksByPillar,
  editableTaskValues,
  selectedModerations,
  isSubmitting,
  submitError,
  onToggleTask,
  onTaskInputChange,
  onToggleModeration,
  onBack,
  onConfirm,
  onFinish,
  onExit,
  onRestart,
  variant = 'default',
}: {
  language: OnboardingLanguage;
  gameMode: GameMode;
  routeStepId: StepId;
  selectedTasksByPillar: Record<OnboardingPillar, string[]>;
  editableTaskValues: Record<string, string>;
  selectedModerations: ModerationOption[];
  isSubmitting: boolean;
  submitError: string | null;
  onToggleTask: (pillar: OnboardingPillar, taskId: string) => void;
  onTaskInputChange: (taskKey: string, value: string) => void;
  onToggleModeration: (value: ModerationOption) => void;
  onBack: () => void;
  onConfirm: () => void;
  onFinish: () => void;
  onExit: () => void;
  onRestart: () => void;
  variant: 'default' | 'onboarding2';
}) {
  const quickStartGp = computeQuickStartGp(selectedTasksByPillar);
  const route = [
    'quick-start-body',
    'quick-start-mind',
    'quick-start-soul',
    ...(selectedTasksByPillar.Body.includes('MODERACION') ? ['quick-start-moderation'] : []),
    'quick-start-summary',
  ] as StepId[];
  const stepIndex = Math.max(0, route.indexOf(routeStepId));
  const renderPillarStep = (pillar: OnboardingPillar) => (
    <QuickStartTasksStep
      language={language}
      pillar={pillar}
      tasks={QUICK_START_TASKS[language][pillar]}
      selectedIds={selectedTasksByPillar[pillar]}
      inputValues={editableTaskValues}
      minimum={variant === 'onboarding2' ? 3 : QUICK_START_MINIMUMS[gameMode]}
      gameMode={gameMode}
      balancedBonusActive={quickStartGp.balancedBonusActive}
      selectedTasksByPillar={selectedTasksByPillar}
      variant={variant}
      onToggleTask={(taskId) => onToggleTask(pillar, taskId)}
      onInputChange={(taskId, value) => onTaskInputChange(`${pillar}-${taskId}`, value)}
      onBack={onBack}
      onConfirm={onConfirm}
    />
  );

  return (
    <div className={`quickstart-premium-root onboarding-premium-root ${variant === 'onboarding2' ? 'onboarding2-premium-root' : ''} min-h-screen min-h-dvh pb-12 pt-28 text-white sm:pt-32`}>
      <HUD
        language={language}
        mode={gameMode}
        stepIndex={stepIndex}
        totalSteps={route.length}
        xp={quickStartGp.xp}
        onRestart={onRestart}
        onExit={onExit}
      />

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
        {routeStepId === 'quick-start-body' ? renderPillarStep('Body') : null}
        {routeStepId === 'quick-start-mind' ? renderPillarStep('Mind') : null}
        {routeStepId === 'quick-start-soul' ? renderPillarStep('Soul') : null}
        {routeStepId === 'quick-start-moderation' ? (
          <QuickStartModerationStep
            language={language}
            selectedModerations={selectedModerations}
            onToggle={onToggleModeration}
            onBack={onBack}
            onConfirm={onConfirm}
          />
        ) : null}
        {routeStepId === 'quick-start-summary' ? (
          <QuickStartSummaryStep
            language={language}
            gameMode={gameMode}
            selectedByPillar={selectedTasksByPillar}
            tasksByPillar={QUICK_START_TASKS[language]}
            xp={quickStartGp.xp}
            onBack={onBack}
            onConfirm={onFinish}
            loading={isSubmitting}
            submitError={submitError}
          />
        ) : null}
      </div>
    </div>
  );
}

export function IntegratedQuickStartFlow({
  language: initialLanguage = 'es',
  gameMode: initialGameMode,
  avatarId,
  onBackToPathSelect,
  onExit,
  onRestart,
  routeStepId,
  selectedTasksByPillar: controlledSelectedTasksByPillar,
  editableTaskValues: controlledEditableTaskValues,
  selectedModerations: controlledSelectedModerations,
  isSubmitting: controlledIsSubmitting = false,
  submitError: controlledSubmitError = null,
  onToggleTask,
  onTaskInputChange,
  onToggleModeration,
  onBack,
  onConfirm,
  onFinish,
  variant = 'default',
}: IntegratedQuickStartFlowProps) {
  if (routeStepId) {
    return (
      <ControlledQuickStartFlow
        language={initialLanguage}
        gameMode={initialGameMode}
        routeStepId={routeStepId}
        selectedTasksByPillar={controlledSelectedTasksByPillar ?? { Body: [], Mind: [], Soul: [] }}
        editableTaskValues={controlledEditableTaskValues ?? {}}
        selectedModerations={controlledSelectedModerations ?? []}
        isSubmitting={controlledIsSubmitting}
        submitError={controlledSubmitError}
        onToggleTask={onToggleTask ?? (() => undefined)}
        onTaskInputChange={onTaskInputChange ?? (() => undefined)}
        onToggleModeration={onToggleModeration ?? (() => undefined)}
        onBack={onBack ?? onBackToPathSelect}
        onConfirm={onConfirm ?? (() => undefined)}
        onFinish={onFinish ?? onConfirm ?? (() => undefined)}
        onExit={onExit}
        onRestart={onRestart}
        variant={variant}
      />
    );
  }

  return (
    <UncontrolledIntegratedQuickStartFlow
      language={initialLanguage}
      gameMode={initialGameMode}
      avatarId={avatarId}
      onBackToPathSelect={onBackToPathSelect}
      onExit={onExit}
      onRestart={onRestart}
    />
  );
}

function UncontrolledIntegratedQuickStartFlow({
  language: initialLanguage = 'es',
  gameMode: initialGameMode,
  onBackToPathSelect,
  onExit,
  onRestart,
}: Pick<IntegratedQuickStartFlowProps, 'language' | 'gameMode' | 'avatarId' | 'onBackToPathSelect' | 'onExit' | 'onRestart'>) {
  const { theme } = useThemePreference();
  const isLight = theme === 'light';
  const navigate = useNavigate();
  const language = initialLanguage;
  const [step, setStep] = useState<Step>('body');
  const gameMode = initialGameMode;
  const [selectedByPillar, setSelectedByPillar] = useState<Record<Pillar, string[]>>({ Body: [], Mind: [], Soul: [] });
  const [inputsByTask, setInputsByTask] = useState<Record<string, string>>({});
  const [moderationPrefs, setModerationPrefs] = useState<Record<ModerationOption, boolean>>({
    sugar: false,
    tobacco: false,
    alcohol: false,
  });
  const [setupProgress, setSetupProgress] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitCompleted, setSubmitCompleted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showGrowthPointsModal, setShowGrowthPointsModal] = useState(false);
  const [pendingGrowthPointsModal, setPendingGrowthPointsModal] = useState(true);
  const copy = COPY[language];

  const minimum = QUICK_START_MINIMUMS[gameMode];
  const setupSteps = useMemo(
    () => copy.setupSteps.map((setupStep) => setupStep.replace('{mode}', copy.modeLabels[gameMode])),
    [copy.modeLabels, copy.setupSteps, gameMode],
  );
  const selectedCounts = {
    Body: selectedByPillar.Body.length,
    Mind: selectedByPillar.Mind.length,
    Soul: selectedByPillar.Soul.length,
  };

  const balancedBonusActive = selectedCounts.Body > 0
    && selectedCounts.Body === selectedCounts.Mind
    && selectedCounts.Mind === selectedCounts.Soul;

  const baseTotalGp = (selectedCounts.Body + selectedCounts.Mind + selectedCounts.Soul) * 3;
  const totalGp = balancedBonusActive ? Math.ceil(baseTotalGp * 1.5) : baseTotalGp;

  const visibleRoute = useMemo(
    () => buildVisibleRoute(selectedByPillar.Body.includes('MODERACION')),
    [selectedByPillar.Body],
  );

  const currentStepIndex = Math.max(0, visibleRoute.indexOf(step));
  const currentPillar = step === 'body' ? 'Body' : step === 'mind' ? 'Mind' : step === 'soul' ? 'Soul' : null;

  const quickStartDraft = useMemo<QuickStartPreviewDraft>(() => {
    const selectedTraitsByPillar = (Object.keys(selectedByPillar) as Pillar[]).reduce<Record<Pillar, string[]>>((acc, pillar) => {
      const taskById = new Map(copy.tasks[pillar].map((task) => [task.id, task.trait]));
      acc[pillar] = selectedByPillar[pillar].map((id) => taskById.get(id) ?? id).filter((trait): trait is string => Boolean(trait));
      return acc;
    }, { Body: [], Mind: [], Soul: [] });

    const completedSteps: Partial<Record<Step, boolean>> = {};
    visibleRoute.forEach((routeStep, index) => {
      completedSteps[routeStep] = index <= currentStepIndex;
    });

    return {
      selectedGameMode: gameMode,
      onboardingPath: 'quick_start',
      selectedTasksByPillar: selectedByPillar,
      selectedTraitsByPillar,
      editableTaskValues: inputsByTask,
      selectedModerations: (Object.keys(moderationPrefs) as ModerationOption[]).filter((option) => moderationPrefs[option]),
      language,
      gpSummaryPreview: {
        byPillar: { Body: selectedCounts.Body * 3, Mind: selectedCounts.Mind * 3, Soul: selectedCounts.Soul * 3 },
        total: totalGp,
      },
      balanceBonusPreview: {
        active: balancedBonusActive,
        multiplier: balancedBonusActive ? 1.5 : 1,
      },
      completedSteps,
    };
  }, [balancedBonusActive, copy.tasks, currentStepIndex, gameMode, inputsByTask, language, moderationPrefs, selectedByPillar, selectedCounts.Body, selectedCounts.Mind, selectedCounts.Soul, totalGp, visibleRoute]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [step]);

  useEffect(() => {
    if (step === 'body' && pendingGrowthPointsModal) {
      setShowGrowthPointsModal(true);
      setPendingGrowthPointsModal(false);
    }
  }, [pendingGrowthPointsModal, step]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (showGrowthPointsModal) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showGrowthPointsModal]);

  useEffect(() => {
    if (step === 'setup') {
      setSetupProgress(1);
      const timer = window.setInterval(() => {
        setSetupProgress((prev) => {
          if (prev >= setupSteps.length) {
            window.clearInterval(timer);
            return prev;
          }
          return prev + 1;
        });
      }, 950);

      return () => window.clearInterval(timer);
    }

    return undefined;
  }, [setupSteps.length, step]);

  const handleToggleTask = (pillar: Pillar, taskId: string) => {
    setSelectedByPillar((prev) => {
      const current = prev[pillar];
      const exists = current.includes(taskId);
      const next = exists ? current.filter((id) => id !== taskId) : [...current, taskId];
      return { ...prev, [pillar]: next };
    });
  };

  const goNext = () => {
    const currentIndex = visibleRoute.indexOf(step);
    const nextStep = visibleRoute[currentIndex + 1];
    if (nextStep) {
      setStep(nextStep);
    }
  };

  const goBack = () => {
    const currentIndex = visibleRoute.indexOf(step);
    const previousStep = visibleRoute[currentIndex - 1];
    if (previousStep) {
      setStep(previousStep);
      return;
    }
    onBackToPathSelect();
  };

  const canContinue = (() => {
    if (!currentPillar) {
      return true;
    }
    return selectedByPillar[currentPillar].length >= minimum;
  })();

  const submitQuickStart = async () => {
    if (isSubmitting) {
      return;
    }

    console.info('[quick_start]', {
      event: 'quick_start submit started',
      step,
      gameMode,
      selectedCounts,
      balancedBonusActive,
      totalGp,
    });

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const user = await getCurrentUserProfile();
      const taskMap = (Object.keys(copy.tasks) as Pillar[]).reduce<Map<string, Task>>((map, pillar) => {
        for (const task of copy.tasks[pillar]) {
          map.set(`${pillar}-${task.id}`, task);
        }
        return map;
      }, new Map());

      const manualCandidates = (Object.keys(selectedByPillar) as Pillar[]).flatMap((pillar) =>
        selectedByPillar[pillar].map((taskId) => {
          const key = `${pillar}-${taskId}`;
          const task = taskMap.get(key);
          const inputValue = (inputsByTask[key] ?? '').trim();
          return {
            task: task?.text ?? taskId,
            pillar_code: pillar.toUpperCase(),
            trait_code: taskId.toUpperCase(),
            input_value: inputValue || undefined,
            metadata: {
              task_id: taskId,
              task_prefix: task?.text,
              task_input_before: task?.inputBefore,
              task_input_after: task?.inputAfter,
            },
          };
        }),
      );

      const foundations = {
        body: selectedByPillar.Body,
        mind: selectedByPillar.Mind,
        soul: selectedByPillar.Soul,
        bodyOpen: '',
        mindOpen: '',
        soulOpen: '',
      };

      console.info('[quick_start]', {
        event: 'quick_start intro payload summary',
        onboardingPath: 'quick_start',
        gameMode,
        manualCandidates: manualCandidates.length,
        selectedTasksByPillar: {
          body: selectedByPillar.Body.length,
          mind: selectedByPillar.Mind.length,
          soul: selectedByPillar.Soul.length,
        },
        moderationSelected: (Object.keys(moderationPrefs) as ModerationOption[]).filter((option) => moderationPrefs[option]).length,
        balancedBonusActive,
        balanceBonusMultiplier: balancedBonusActive ? 1.5 : 1,
      });

      const response = await apiAuthorizedFetch('/onboarding/intro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ts: new Date().toISOString(),
          client_id: crypto.randomUUID(),
          email: (user.email_primary ?? `${user.clerk_user_id}@innerbloom.local`).toLowerCase(),
          mode: gameMode,
          data: {
            low: { body: [], soul: [], mind: [], note: '' },
            chill: { oneThing: '', motiv: [] },
            flow: { goal: '', imped: [] },
            evolve: { goal: '', trade: [], att: '' },
            foundations,
            quick_start: {
              selected_moderations: (Object.keys(moderationPrefs) as ModerationOption[]).filter((option) => moderationPrefs[option]),
              selected_tasks_by_pillar: {
                body: selectedByPillar.Body,
                mind: selectedByPillar.Mind,
                soul: selectedByPillar.Soul,
              },
              manual_task_candidates: manualCandidates,
            },
          },
          xp: {
            total: totalGp,
            Body: selectedCounts.Body * 3,
            Mind: selectedCounts.Mind * 3,
            Soul: selectedCounts.Soul * 3,
          },
          meta: {
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            lang: language,
            device: /mobi|android/i.test(window.navigator.userAgent) ? 'mobile' : 'desktop',
            version: 'quick-start-v1',
            user_id: user.clerk_user_id,
            onboarding_path: 'quick_start',
          },
        }),
      });

      if (!response.ok) {
        throw new Error('No se pudo guardar Quick Start');
      }

      const introJson = (await response.json()) as {
        ok?: boolean;
        session_id?: string;
        taskgen_correlation_id?: string | null;
      };

      console.info('[quick_start]', {
        event: 'quick_start intro accepted',
        ok: introJson.ok ?? null,
        sessionId: introJson.session_id ?? null,
        taskgenCorrelationId: introJson.taskgen_correlation_id ?? null,
      });

      if (visibleRoute.includes('moderation')) {
        await markOnboardingProgress('moderation_modal_shown', { trigger: 'quick_start' });
        await markOnboardingProgress('moderation_modal_resolved', { trigger: 'quick_start' });
      }

      setJourneyGenerationPending({
        clerkUserId: user.clerk_user_id,
        gameMode,
      });
      setSubmitCompleted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'No se pudo guardar Quick Start');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSummaryConfirm = () => {
    setStep('setup');
    void submitQuickStart();
  };

  const openGuidedDemo = () => {
    if (!submitCompleted) {
      return;
    }
    navigate(buildDemoUrl({ language, source: 'onboarding', mode: 'onboarding' }));
  };

  const quickStartBody = () => {
    if (!currentPillar) {
      return null;
    }

    const tasks = copy.tasks[currentPillar];

    return (
      <section className="quickstart-premium-card onboarding-surface-base mx-auto w-full max-w-3xl rounded-3xl p-5 sm:p-7">
        <header className="mb-5 border-b border-white/10 pb-4">
          <h1 className="text-2xl font-semibold text-white sm:text-3xl">{copy.pillarTitles[currentPillar]}</h1>
          <p className="mt-2 text-sm text-white/70">{copy.pillarSubtitles[currentPillar]}</p>
          <div
            className="quickstart-min-rule mt-3 inline-flex max-w-full items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold"
          >
            <span className="whitespace-nowrap">{copy.minRule(minimum)}</span>
            <span className="quickstart-min-rule__dot" aria-hidden>·</span>
            <span className="whitespace-nowrap">{copy.suggestedRule(gameMode)}</span>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={`quickstart-bonus-pill inline-flex text-[0.68rem] font-medium sm:text-[0.72rem] ${
                balancedBonusActive ? 'quickstart-bonus-pill--ready' : 'quickstart-bonus-pill--pending'
              }`}
            >
              {balancedBonusActive ? copy.bonusReady : copy.bonusPending}
            </span>
          </div>
        </header>

        <div className="space-y-3">
          {tasks.map((task) => {
            const selected = selectedByPillar[currentPillar].includes(task.id);
            return (
              <InlineTaskRow
                key={task.id}
                task={task}
                selected={selected}
                inputValue={inputsByTask[`${currentPillar}-${task.id}`] ?? ''}
                onToggle={() => handleToggleTask(currentPillar, task.id)}
                onInputChange={(value) => {
                  setInputsByTask((prev) => ({ ...prev, [`${currentPillar}-${task.id}`]: value }));
                }}
                copy={copy}
                mode={gameMode}
              />
            );
          })}
        </div>

        {!canContinue ? <p className="mt-4 text-xs text-amber-200">{copy.minimumRequired}</p> : null}

        <NavButtons
          language={language}
          onBack={goBack}
          onConfirm={goNext}
          backLabel={copy.back}
          confirmLabel={copy.continue}
          disabled={!canContinue}
        />
      </section>
    );
  };

  return (
    <div className={`quickstart-premium-root onboarding-premium-root min-h-screen min-h-dvh text-white ${step === 'setup' ? '' : 'pb-12 pt-28 sm:pt-32'}`}>
      {step !== 'setup' ? (
        <HUD
          language={language}
          mode={gameMode}
          stepIndex={currentStepIndex}
          totalSteps={visibleRoute.length}
          xp={{ Body: selectedCounts.Body * 3, Mind: selectedCounts.Mind * 3, Soul: selectedCounts.Soul * 3, total: totalGp }}
          onRestart={onRestart}
          onExit={onExit}
        />
      ) : null}

      <div className={`mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 ${step === 'setup' ? 'py-10' : 'pb-[calc(env(safe-area-inset-bottom)+1.5rem)]'}`}>
        {/* Payload shape kept aligned with the real Quick Start flow. No SQL persistence in this iteration. */}
        <pre className="hidden" aria-hidden>{JSON.stringify(quickStartDraft, null, 2)}</pre>
        {step === 'body' || step === 'mind' || step === 'soul' ? quickStartBody() : null}

        {step === 'moderation' ? (
          <section className="quickstart-premium-card onboarding-surface-base mx-auto w-full max-w-3xl rounded-3xl p-5 sm:p-7">
            <h1 className="text-2xl font-semibold text-white sm:text-3xl">{copy.moderationTitle}</h1>
            <p className="mt-2 text-sm text-white/70">{copy.moderationSubtitle}</p>
            <p className="mt-2 text-xs text-white/55">{copy.moderationHint}</p>
            <div className="mt-5 space-y-3">
              {(Object.keys(copy.moderationCards) as ModerationOption[]).map((option) => {
                const card = copy.moderationCards[option];
                const enabled = moderationPrefs[option];
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setModerationPrefs((prev) => ({ ...prev, [option]: !prev[option] }))}
                    className={`quickstart-moderation-option flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition ${enabled ? 'quickstart-moderation-option--enabled' : ''}`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-white">{card.icon} {card.title}</p>
                      <p className="mt-1 text-xs text-white/65">{card.description}</p>
                    </div>
                    <span className={`quickstart-moderation-toggle inline-flex h-5 w-10 shrink-0 items-center overflow-hidden rounded-full p-0.5 ${enabled ? 'quickstart-moderation-toggle--enabled' : ''}`}>
                      <span className={`block h-4 w-4 shrink-0 rounded-full bg-white transition-transform ${enabled ? 'translate-x-[1.125rem]' : ''}`} />
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-white/65">{copy.moderationFlex}</p>
            <NavButtons language={language} onBack={goBack} onConfirm={goNext} backLabel={copy.back} confirmLabel={copy.continue} />
          </section>
        ) : null}

        {step === 'summary' ? (
          <section className="quickstart-premium-card glass-card onboarding-surface-base mx-auto w-full max-w-5xl rounded-3xl p-6 sm:p-8">
            <header className="flex flex-col gap-2 border-b border-white/5 pb-4">
              <p className="text-xs uppercase tracking-[0.35em] text-white/50">{copy.quickSummary.eyebrow}</p>
              <h2 className="text-3xl font-semibold text-white">{copy.quickSummary.title}</h2>
              <p className="text-sm text-white/70">{copy.quickSummary.subtitle}</p>
            </header>
            <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
              <div className="space-y-5">
                <section className="quickstart-premium-surface rounded-2xl border p-5">
                  <header className="border-b border-white/5 pb-3">
                    <p className="text-xs uppercase tracking-[0.35em] text-white/50">{copy.quickSummary.baseData}</p>
                  </header>
                  <div className="mt-4 space-y-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/50">{copy.quickSummary.gameMode}</p>
                      <p className="mt-1 text-sm font-semibold uppercase tracking-[0.18em] text-white">{gameMode}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/50">{copy.quickSummary.state}</p>
                      <p className="mt-1 text-sm text-white/80">{GAME_MODE_META[GAME_MODE_META_KEY[gameMode]].state[language]}</p>
                    </div>
                  </div>
                </section>
                <section className="quickstart-premium-surface rounded-2xl border p-5">
                  <header className="border-b border-white/5 pb-3">
                    <p className="text-xs uppercase tracking-[0.35em] text-white/50">{copy.quickSummary.pillars}</p>
                  </header>
                  <div className="mt-4 space-y-3">
                    <p className="text-sm text-white/70">{copy.quickSummary.selectedTraits}</p>
                    {(['Body', 'Mind', 'Soul'] as Pillar[]).map((pillar) => (
                      <div key={pillar} className="quickstart-premium-surface rounded-xl border p-3">
                        <p className="text-sm font-semibold text-white">{copy.pillarTitles[pillar]}</p>
                        <p className="mt-1 text-xs text-white/70">{copy.quickSummary.selectedTasks}: {selectedByPillar[pillar].length}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {quickStartDraft.selectedTraitsByPillar[pillar].length > 0
                            ? quickStartDraft.selectedTraitsByPillar[pillar].map((trait) => (
                              <span key={`${pillar}-${trait}`} className="quickstart-pill rounded-full border px-2.5 py-1 text-[11px] font-medium">
                                {trait}
                              </span>
                            ))
                            : <span className="text-xs text-white/50">—</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
              <aside className="space-y-5">
                <section className="quickstart-premium-surface rounded-2xl border p-5">
                  <header className="border-b border-white/5 pb-3">
                    <p className="text-xs uppercase tracking-[0.35em] text-white/50">{copy.quickSummary.xp}</p>
                  </header>
                  <div className="mt-4 space-y-2 text-sm text-white/85">
                    <p><span className="font-semibold text-white">Body 🫀:</span> {selectedCounts.Body * 3} GP</p>
                    <p><span className="font-semibold text-white">Mind 🧠:</span> {selectedCounts.Mind * 3} GP</p>
                    <p><span className="font-semibold text-white">Soul 🏵️:</span> {selectedCounts.Soul * 3} GP</p>
                    <p className="mt-3 text-base font-semibold text-white">{copy.quickSummary.total}: {totalGp} GP</p>
                    <p className="text-xs text-white/60">{copy.quickSummary.baseHint}</p>
                  </div>
                </section>
              </aside>
            </div>
            <NavButtons
              language={language}
              onBack={goBack}
              onConfirm={handleSummaryConfirm}
              backLabel={copy.back}
              confirmLabel={copy.quickSummary.start}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </section>
        ) : null}

        {step === 'setup' ? (
          <section className="quickstart-premium-card relative mx-auto mt-5 w-full max-w-3xl rounded-3xl p-5 sm:p-8">
            <div className={`mb-5 flex items-center justify-center gap-2 text-center text-[0.68rem] font-semibold uppercase tracking-[0.36em] sm:text-xs ${isLight ? 'text-slate-500' : 'text-white/65'}`}>
              <span>Innerbloom</span>
              <img src="/IB-COLOR-LOGO-v2.png" alt="Innerbloom logo" className="h-[1.8em] w-auto" />
            </div>
            <h1 className={`text-balance text-2xl font-semibold sm:text-3xl ${isLight ? 'text-slate-900' : 'text-white'}`}>{copy.setupTitle}</h1>
            <p className={`mt-2 text-sm ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>{copy.setupSubtitle}</p>
            <ul className="mt-6 space-y-3">
              {setupSteps.slice(0, setupProgress).map((setupStep, index) => {
                const complete = setupProgress >= setupSteps.length;
                const isPlanStep = index === setupSteps.length - 1;
                return (
                  <li
                    key={setupStep}
                    className={`flex items-center gap-3 text-sm transition-all duration-500 ${isLight ? 'text-slate-700' : 'text-slate-100/90'}`}
                  >
                    <span className={`quickstart-setup-dot h-2.5 w-2.5 rounded-full ${!complete && index === setupProgress - 1 ? 'animate-pulse' : ''}`} />
                    <span>
                      {setupStep}
                      {isPlanStep ? (
                        <span className="quickstart-free-pill ml-2 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] align-middle">
                          FREE
                        </span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="quickstart-progress-track mt-6 h-1.5 w-full overflow-hidden rounded-full">
              <div
                className={`quickstart-progress-fill h-full transition-all duration-700 ${
                  setupProgress >= setupSteps.length ? 'w-full quick-start-setup__progress-complete' : 'w-1/3 quick-start-setup__progress-animated'
                }`}
              />
            </div>
            <style>{`
              .quick-start-setup__progress-animated {
                animation: quick-start-progress 2.3s ease-in-out infinite;
                transform-origin: left;
              }

              @keyframes quick-start-progress {
                0% {
                  transform: translateX(-112%);
                }

                100% {
                  transform: translateX(318%);
                }
              }
            `}</style>
            <p className={`mt-4 text-sm ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>{copy.setupBridgeHint}</p>
            {setupProgress >= setupSteps.length ? <p className="mt-3 text-sm text-emerald-100">{copy.setupDone}</p> : null}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
              <motion.button
                type="button"
                onClick={openGuidedDemo}
                whileTap={{ scale: 0.97 }}
                className="quickstart-primary-cta order-1 inline-flex items-center justify-center rounded-full border px-6 py-2.5 text-sm font-semibold transition duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 sm:order-2"
                disabled={isSubmitting || !submitCompleted}
              >
                {isSubmitting
                  ? language === 'en' ? 'Saving...' : 'Guardando...'
                  : copy.setupCta}
              </motion.button>
            </div>
            {submitError ? <p className="mt-4 text-sm text-rose-200">{submitError}</p> : null}
          </section>
        ) : null}
      </div>

      {showGrowthPointsModal ? (
        <GpExplainerOverlay
          language={language}
          onClose={() => {
            setShowGrowthPointsModal(false);
          }}
        />
      ) : null}
    </div>
  );
}
